import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { inr, initials } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { FormModal } from "@/components/pawkt/FormModal";
import { SearchInput } from "@/components/pawkt/SearchInput";
import { Button } from "@/components/ui/button";
import { Plus, UserCog } from "lucide-react";
import { toast } from "sonner";
import { StaffScheduleButton } from "@/components/pawkt/StaffSchedule";
import { EditStaffButton } from "@/components/pawkt/EditStaff";
import { hasPerm } from "@/lib/perms";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["manager", "receptionist", "vet", "groomer", "trainer", "handler", "accountant", "sales"].map((r) => ({ value: r, label: r }));

export default function Staff() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = hasPerm(user, "staff.edit");
  const [open, setOpen] = useState(false);
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get("search") || "");
  const { data, isLoading } = useQuery({ queryKey: ["staff", search], queryFn: async () => (await api.get(`/staff${search ? `?search=${encodeURIComponent(search)}` : ""}`)).data });
  const perf = useQuery({ queryKey: ["staff-perf-summary", "period=this_month"], enabled: hasPerm(user, "reports.view"), retry: false, queryFn: async () => (await api.get("/staff/performance/summary?period=this_month")).data });
  const pm = Object.fromEntries((perf.data?.staff || []).map((x) => [x.id, x]));
  const add = useMutation({ mutationFn: (b) => api.post("/staff", b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["staff"] }); setOpen(false); toast.success("Staff added"); }, onError: (e) => toast.error(apiErr(e)) });

  return (
    <div>
      <PageHeader title="Staff" subtitle="Your team, roles and performance">
        {hasPerm(user, "staff.create") && <Button onClick={() => setOpen(true)} data-testid="add-staff-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"><Plus className="h-4 w-4" /> Add Staff</Button>}
      </PageHeader>

      <div className="mb-4 max-w-md">
        <SearchInput value={search} onChange={setSearch} testid="staff-search" placeholder="Search staff by name, role or mobile…" />
      </div>

      {isLoading ? <Loading /> : (data?.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.map((s) => (
            <div key={s.id} data-testid={`staff-${s.id}`} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-semibold">{initials(s.name)}</div>
                <div className="min-w-0 flex-1"><div className="font-semibold text-slate-900">{s.name}</div><div className="text-xs text-slate-400 capitalize">{s.role}{s.department ? ` · ${s.department}` : ""}{s.active === false ? " · inactive" : ""}</div></div>
                {canEdit && <EditStaffButton staff={s} />}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-center border-t border-slate-100 pt-3">
                <div><div data-testid={`staff-net-revenue-${s.id}`} className="font-display font-bold text-slate-900">{pm[s.id] ? inr(pm[s.id].net_revenue) : "—"}</div><div className="text-[10px] uppercase text-slate-400">Net revenue · this month</div></div>
                <div><div data-testid={`staff-services-${s.id}`} className="font-display font-bold text-slate-900">{pm[s.id] ? pm[s.id].services_completed : "—"}</div><div className="text-[10px] uppercase text-slate-400">Services · this month</div></div>
              </div>
              {canEdit && s.commission_pct > 0 && <div className="mt-2 text-xs text-slate-400 text-center">Commission {s.commission_pct}%</div>}
              <StaffScheduleButton staff={s} />
            </div>
          ))}
        </div>
      ) : <Empty icon={UserCog} title="No staff yet" />)}

      <FormModal open={open} onOpenChange={setOpen} title="Add Staff" testid="staff-modal"
        initial={{ role: "groomer", commission_pct: 0, salary: 0 }} submitLabel="Add staff"
        fields={[
          { name: "name", label: "Full name", full: true },
          { name: "role", label: "Role", type: "select", options: ROLES },
          { name: "phone", label: "Phone" },
          { name: "email", label: "Email" },
          ...(canEdit ? [{ name: "salary", label: "Monthly salary (₹)", type: "number" },
            { name: "commission_pct", label: "Commission %", type: "number" }] : []),
        ]}
        onSubmit={(v) => add.mutateAsync(v)} />
    </div>
  );
}
